import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

import {
  getConfiguration,
  getHotels,
  getPaymentConfiguration,
  type ConfigurationResponse,
  type HotelsResponse,
  type PaymentConfigurationResponse,
  type MewsApiError,
} from "@/services/mews-api";

type Nullable<T> = T | null;

interface MewsConfigurationContextValue {
  configuration: Nullable<ConfigurationResponse>;
  hotels: Nullable<HotelsResponse>;
  paymentConfiguration: Nullable<PaymentConfigurationResponse>;
  isLoading: boolean;
  isLoaded: boolean;
  error: Nullable<MewsApiError>;
  /** Reloads all configuration data (bypasses cache). */
  reload: () => Promise<void>;
}

const MewsConfigurationContext =
  createContext<MewsConfigurationContextValue | null>(null);

interface MewsConfigurationProviderProps {
  children: ReactNode;
}

export const MewsConfigurationProvider: React.FC<
  MewsConfigurationProviderProps
> = ({ children }) => {
  const [configuration, setConfiguration] =
    useState<Nullable<ConfigurationResponse>>(null);
  const [hotels, setHotels] = useState<Nullable<HotelsResponse>>(null);
  const [paymentConfiguration, setPaymentConfiguration] =
    useState<Nullable<PaymentConfigurationResponse>>(null);
  const [error, setError] = useState<Nullable<MewsApiError>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const load = useCallback(
    async (useCache: boolean) => {
      setIsLoading(true);
      setError(null);

      try {
        const [configRes, hotelsRes, paymentRes] = await Promise.all([
          getConfiguration(useCache),
          getHotels(undefined, useCache),
          getPaymentConfiguration(undefined, useCache),
        ]);

        if (configRes.data) {
          setConfiguration(configRes.data);
        }
        if (hotelsRes.data) {
          setHotels(hotelsRes.data);
        }
        if (paymentRes.data) {
          setPaymentConfiguration(paymentRes.data);
        }

        // Prefer configuration error, then hotels, then payment
        const firstError = configRes.error || hotelsRes.error || paymentRes.error || null;
        setError(firstError);
      } catch (e) {
        // Network or unexpected error
        setError({
          Message:
            e instanceof Error
              ? e.message
              : "Failed to load Mews configuration.",
        });
      } finally {
        setIsLoading(false);
        setIsLoaded(true);
      }
    },
    [],
  );

  useEffect(() => {
    // Initial load uses cache where available
    void load(true);
  }, [load]);

  const reload = useCallback(async () => {
    await load(false);
  }, [load]);

  return (
    <MewsConfigurationContext.Provider
      value={{
        configuration,
        hotels,
        paymentConfiguration,
        isLoading,
        isLoaded,
        error,
        reload,
      }}
    >
      {children}
    </MewsConfigurationContext.Provider>
  );
};

export const useMewsConfiguration = (): MewsConfigurationContextValue => {
  const ctx = useContext(MewsConfigurationContext);
  if (!ctx) {
    throw new Error(
      "useMewsConfiguration must be used within a MewsConfigurationProvider",
    );
  }
  return ctx;
};

